import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState('')
  const { signIn, signInWithGoogle, loading } = useAuthStore()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      await signIn(email, password)
      navigate('/timeline')
    } catch (err: any) {
      setError(err.message ?? 'Sign in failed')
    }
  }

  return (
    <div className="min-h-dvh bg-bg flex flex-col px-6 pt-16 pb-8 safe-top">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-8 max-w-sm mx-auto w-full"
      >
        {/* Header */}
        <div className="flex flex-col items-center gap-3">
          <img src="/icon.png" alt="Timeline" className="w-16 h-16 rounded-2xl" />
          <h1 className="font-serif text-3xl font-bold text-white">Welcome back</h1>
          <p className="text-white/40 text-sm text-center">Your timeline is waiting.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input-field pl-10"
              required
              autoComplete="email"
            />
          </div>

          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input-field pl-10 pr-10"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && (
            <p className="text-rose text-sm text-center bg-rose/10 border border-rose/20 rounded-xl px-4 py-2">
              {error}
            </p>
          )}

          <button type="submit" className="btn-primary mt-2" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-white/[0.07]" />
          <span className="text-white/20 text-xs font-mono">or</span>
          <div className="flex-1 h-px bg-white/[0.07]" />
        </div>

        {/* OAuth */}
        <button
          onClick={() => signInWithGoogle()}
          className="btn-secondary flex items-center justify-center gap-2"
        >
          <span>🌐</span> Continue with Google
        </button>

        {/* Footer */}
        <p className="text-center text-white/30 text-sm">
          No account yet?{' '}
          <Link to="/signup" className="text-blue-light underline">Create one</Link>
        </p>
      </motion.div>
    </div>
  )
}
